import { Box, Flex, FormControl, Stack, Text } from '@chakra-ui/react';
import { MultiValue, SingleValue } from 'chakra-react-select';
import debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';

import Button from 'components/atoms/button';
import { FieldTitle } from 'components/atoms/form/fieldTitle';
import Heading from 'components/atoms/heading';
import { SalaryRange, SalarySuffixTypes } from 'components/atoms/salaryRange';
import { RangeSlider } from 'components/molecules/form/rangeSlider';
import {
  AsyncSelect,
  SelectOption,
} from 'components/molecules/jobs/filters/asyncSelect';
import { AsyncSelectFocus } from 'components/molecules/jobs/filters/asyncSelectFocus';
import { Options } from 'components/molecules/jobs/filters/options';
import { jobExperienceLevelOptions } from 'lib/constants/enums/jobExperienceLevel';
import {
  CompanyLocationEnum,
  Focus,
  SeniorityEnum,
  useSearchCitiesLazyQuery,
  useSearchTechsLazyQuery,
  WorkModeEnum,
} from 'lib/generated/graphql';
import { useJobFilters } from 'lib/repositories/jobsShowcaseFilters';

export interface DesktopFiltersProps {
  handleSearchJobs(): void;
  defaultSalaryValues: Array<number>;
}

export const DesktopFilters = ({
  handleSearchJobs,
  defaultSalaryValues,
}: DesktopFiltersProps) => {
  const {
    filters,
    setFilters,
    setCompanyLocation,
    setCity,
    setTechs,
    setType,
    setWorkMode,
    setSeniority,
  } = useJobFilters();
  const [getCities] = useSearchCitiesLazyQuery();
  const [getTechs] = useSearchTechsLazyQuery();

  const [salaryRangeValue, setSalaryRangeValue] = useState<number[]>([
    defaultSalaryValues[0],
    defaultSalaryValues[1],
  ]);

  const showResetFilter =
    filters.city ||
    (filters.techs && filters.techs?.length > 0) ||
    (filters.workMode !== undefined && filters.workMode.length > 0) ||
    (filters.type !== undefined && filters.type.length > 0) ||
    (filters.seniority !== undefined && filters.seniority.length > 0) ||
    (filters.companyLocation !== undefined &&
      filters.companyLocation?.length > 0) ||
    (filters.minSalary && filters.minSalary !== defaultSalaryValues[0]) ||
    (filters.minSalary && filters.maxSalary !== defaultSalaryValues[1]);

  const handleSalaryChange = (value: number[]) => {
    setSalaryRangeValue(value);
  };

  const handleSalaryChangeEnd = ([min, max]: number[]) =>
    setFilters({ ...filters, maxSalary: max, minSalary: min });

  const handleCityChange = (
    newValue: MultiValue<SelectOption> | SingleValue<SelectOption>
  ) => setCity(newValue as SelectOption);
  const handleTechsChange = (
    newValue: MultiValue<SelectOption> | SingleValue<SelectOption>
  ) => setTechs(newValue as SelectOption[]);
  const handleSeniorityChange = (e: SeniorityEnum[]) => setSeniority(e);
  const handleWorkModeChange = (e: WorkModeEnum[]) => setWorkMode(e);
  const handleCompanyLocationChange = (e: CompanyLocationEnum[]) => {
    if (JSON.stringify(e) === JSON.stringify([CompanyLocationEnum.Us])) {
      filters.workMode = [];
      filters.city = null;
    }
    setCompanyLocation(e);
  };

  const resetFilter = () => {
    filters.companyLocation = [];
    filters.city = null;
    filters.type = [];
    filters.techs = null;
    filters.focuses = [];
    handleTechsChange(null);
    filters.workMode = [];
    filters.seniority = [];
    [filters.minSalary, filters.maxSalary] = defaultSalaryValues;
    setSalaryRangeValue(defaultSalaryValues);
    handleSalaryChangeEnd(defaultSalaryValues);
  };

  const loadCityOptions = debounce(
    (value: string, callBack: (options: SelectOption[]) => void) =>
      getCities({ variables: { textSearch: value } }).then((response) =>
        callBack(
          response.data?.searchCities.map((tech) => ({
            text: tech.name,
            value: tech.id,
          })) || []
        )
      ),
    500
  );

  const loadTechOptions = debounce(
    (value: string, callback: (options: SelectOption[]) => void) => {
      getTechs({ variables: { textSearch: value } }).then((response) =>
        callback(
          response.data?.searchTechs.map((tech) => ({
            text: tech.name,
            value: tech.id,
          })) || []
        )
      );
    },
    500
  );

  useEffect(() => {
    setSalaryRangeValue([defaultSalaryValues[0], defaultSalaryValues[1]]);
  }, [defaultSalaryValues]);

  const worksWithSkills = (focuses: Focus[]) =>
    focuses?.some((focus) => focus.career?.worksWithSkills);

  const showSkillFilter =
    !filters.focuses ||
    filters.focuses?.length === 0 ||
    worksWithSkills(filters.focuses);

  return (
    <Stack
      direction='column'
      bgColor='white'
      borderRadius={2}
      h='max-content'
      p={8}
      w='full'
      spacing={6}
      align='flex-start'
    >
      <Flex alignItems='center'>
        <Heading
          color='text.900'
          fontSize='lg'
          letterSpacing='normal'
          fontWeight={700}
          format='h4'
          pt={0}
          m={0}
        >
          Filtre sua busca
        </Heading>
        {showResetFilter && (
          <Text
            ml={2}
            fontSize='xs'
            fontWeight={400}
            letterSpacing='tight'
            color='primary.500'
            textDecoration='underline'
            cursor='pointer'
            onClick={resetFilter}
          >
            Limpar
          </Text>
        )}
      </Flex>

      <FormControl>
        <FieldTitle
          mb={1}
          label='Localização da empresa'
          fontSize={14}
          fontWeight={700}
          color='text.900'
        />
        <Options
          value={filters.companyLocation}
          options={[
            { label: 'Brasil', value: CompanyLocationEnum.Brazil },
            { label: 'Fora do Brasil', value: CompanyLocationEnum.Us },
          ]}
          onChange={handleCompanyLocationChange}
        />
      </FormControl>

      <FormControl>
        <AsyncSelectFocus onlyActiveJobs />

        {showSkillFilter && (
          <FieldTitle
            label='Habilidades'
            fontSize={14}
            fontWeight={700}
            mt={4}
            mb={2}
            color='text.900'
          />
        )}
        {showSkillFilter && (
          <AsyncSelect
            isMulti
            label='Tecnologias'
            loadOptions={loadTechOptions}
            name='order'
            onChange={handleTechsChange}
            placeholder='Selecione as Habilidades'
            selectedOptionStyle='check'
            value={filters.techs}
            isClearable
            openMenuOnClick
            defaultOptions
          />
        )}
      </FormControl>

      <FormControl>
        <FieldTitle color='text.900' mb={2} label='Tipo de vaga' />

        <Options
          value={filters.type}
          options={[
            { label: 'Remoto', value: 'remote' },
            { label: 'Presencial', value: 'no_remote' },
          ]}
          onChange={(e: string[]) => setType(e)}
        />
      </FormControl>

      {JSON.stringify(filters.companyLocation) !==
        JSON.stringify([CompanyLocationEnum.Us]) && (
        <FormControl>
          <FieldTitle
            label='Cidade da empresa'
            fontSize={14}
            fontWeight={700}
            mb={2}
            color='text.900'
          />

          <AsyncSelect
            label='Cidade da empresa'
            loadOptions={loadCityOptions}
            name='order'
            placeholder='Digite e selecione uma opção'
            selectedOptionStyle='check'
            value={filters.city}
            onChange={handleCityChange}
            isMulti={false}
            isClearable
            openMenuOnClick={false}
            defaultOptions
          />
        </FormControl>
      )}

      <FormControl>
        <Flex alignItems='center' mb={2}>
          <FieldTitle
            color='text.900'
            m={0}
            pr={2}
            label='Nível de Experiência'
          />
        </Flex>

        <Options
          value={filters.seniority}
          options={jobExperienceLevelOptions}
          onChange={handleSeniorityChange}
          stackProps={{ direction: 'column', spacing: 0 }}
        />
      </FormControl>

      <FormControl>
        <FieldTitle color='text.900' mb={2} label='Regime de trabalho' />

        {JSON.stringify(filters.companyLocation) !==
          JSON.stringify([CompanyLocationEnum.Us]) && (
          <Options
            value={filters.workMode}
            options={[
              { label: 'CLT', value: WorkModeEnum.Clt },
              { label: 'PJ ou Cooperativa', value: WorkModeEnum.Pj },
            ]}
            onChange={handleWorkModeChange}
          />
        )}

        {JSON.stringify(filters.companyLocation) ===
          JSON.stringify([CompanyLocationEnum.Us]) && (
          <Options
            value={filters.workMode}
            options={[{ label: 'PJ ou Cooperativa', value: WorkModeEnum.Pj }]}
            onChange={handleWorkModeChange}
          />
        )}
      </FormControl>

      <FormControl>
        <FieldTitle
          color='text.900'
          mb={2}
          label='Faixa de remuneração mensal'
        />

        <SalaryRange
          minSalary={salaryRangeValue[0]}
          maxSalary={salaryRangeValue[1]}
          currency='R$'
          suffix={SalarySuffixTypes.None}
          isFilter
        />

        <Box px={3} mt={4}>
          <RangeSlider
            min={defaultSalaryValues[0]}
            max={defaultSalaryValues[1]}
            onChangeEnd={handleSalaryChangeEnd}
            onChange={handleSalaryChange}
            value={salaryRangeValue}
            defaultValue={[
              filters.minSalary || defaultSalaryValues[0],
              filters.maxSalary || defaultSalaryValues[1],
            ]}
          />
        </Box>
      </FormControl>

      <Button w='full' onClick={() => handleSearchJobs()}>
        Filtrar
      </Button>
    </Stack>
  );
};
